import React from "react";
import { Guide } from "../../types/guide";
import {
    cardStyle,
    emptyTextStyle,
    infoRowStyle,
    mutedTextStyle,
    sectionTitleStyle,
} from "../GuideDetailModal.styles";
import { ChipList, formatBoolean } from "../GuideDetailModal.utils";

export function ExperienceTab({ guide }: { guide: Guide }) {
    const onboarding = guide.onboarding;
    const newExperience = onboarding?.professionalExperience;
    const oldProfessional = onboarding?.legacyProfessional;
    const hasNew = !!newExperience;
    const hasOld = !!oldProfessional;

    if (!hasNew && !hasOld) {
        return <div style={emptyTextStyle}>No experience data submitted yet.</div>;
    }

    return (
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {hasOld && oldProfessional && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12, color: "#2C2731" }}>
                        Basic Professional Info
                    </div>
                    <div style={cardStyle}>
                        <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
                            <div style={infoRowStyle}>
                                <span>Years Practicing</span>
                                <span>{oldProfessional.yearsPracticing ?? "Not provided"}</span>
                            </div>
                            <div style={infoRowStyle}>
                                <span>Employment Type</span>
                                <span>{oldProfessional.employmentType ?? "Not provided"}</span>
                            </div>
                            <div style={infoRowStyle}>
                                <span>Main Income Source</span>
                                <span>{formatBoolean(oldProfessional.isMainIncome)}</span>
                            </div>
                            <div style={infoRowStyle}>
                                <span>Ceremonies Count</span>
                                <span>{oldProfessional.ceremoniesCount ?? "Not provided"}</span>
                            </div>
                            <div style={infoRowStyle}>
                                <span>Has Medical Director</span>
                                <span>{formatBoolean(oldProfessional.hasMedicalDirector)}</span>
                            </div>
                            <div style={infoRowStyle}>
                                <span>Works with Team</span>
                                <span>{formatBoolean(oldProfessional.worksWithOtherFacilitators)}</span>
                            </div>
                            <div style={infoRowStyle}>
                                <span>Gender</span>
                                <span>{oldProfessional.genderId ?? "Not provided"}</span>
                            </div>
                        </div>
                        {oldProfessional.personalRelationship && (
                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(44,39,49,0.12)" }}>
                                <div style={{ ...sectionTitleStyle, fontSize: 12, marginBottom: 4 }}>
                                    Personal Relationship with Psychedelics
                                </div>
                                <div style={{ color: "#403851", fontSize: 12, lineHeight: 1.4 }}>
                                    {oldProfessional.personalRelationship}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {hasNew && newExperience && (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12, color: "#2C2731" }}>
                        Detailed Professional Experience
                    </div>
                    
                    <div style={cardStyle}>
                        <div style={sectionTitleStyle}>Experience Types</div>
                        <ChipList values={newExperience.experienceTypes} empty="Not provided" />
                    </div>

                    <div style={cardStyle}>
                        <div style={sectionTitleStyle}>Legal Status</div>
                        <div style={{ color: "#2C2731", fontSize: 13 }}>{newExperience.legalStatus ?? "Not provided"}</div>
                    </div>

                    <div style={cardStyle}>
                        <div style={sectionTitleStyle}>Support Types</div>
                        <ChipList values={newExperience.supportTypes} empty="Not provided" />
                    </div>

                    <div style={cardStyle}>
                        <div style={sectionTitleStyle}>Travel Willingness</div>
                        <ChipList values={newExperience.travelWillingness} empty="Not provided" />
                    </div>

                    <div style={cardStyle}>
                        <div style={sectionTitleStyle}>Service Locations</div>
                        <ChipList values={newExperience.serviceLocations} empty="Not provided" />
                    </div>

                    <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
                        <div style={cardStyle}>
                            <div style={sectionTitleStyle}>Focus Areas</div>
                            <ChipList values={newExperience.focusAreas} empty="Not provided" />
                        </div>
                        <div style={cardStyle}>
                            <div style={sectionTitleStyle}>Excluded Areas</div>
                            <ChipList values={newExperience.excludedAreas} empty="No exclusions" />
                        </div>
                    </div>

                    <div style={cardStyle}>
                        <div style={sectionTitleStyle}>Works with Medications</div>
                        <div style={{ color: "#2C2731", fontSize: 13 }}>{newExperience.worksWithMedications ?? "Not provided"}</div>
                    </div>

                    <div style={cardStyle}>
                        <div style={sectionTitleStyle}>Special Groups</div>
                        <ChipList values={newExperience.specialGroups} empty="None selected" />
                    </div>

                    <div style={cardStyle}>
                        <div style={sectionTitleStyle}>Religious/Spiritual Beliefs</div>
                        <ChipList values={newExperience.religiousBeliefs} empty="None selected" />
                    </div>
                </div>
            )}
        </div>
    );
}
